import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";

import * as amplitude from "@amplitude/analytics-browser";

import { selectMechanicId } from "../../../redux/auth";

import { useCart } from "../../../hooks/useCart";

import { CART_ITEMS_COLUMNS } from "../../../constants/table";
import { APP_PATHS } from "../../../constants/paths";

import { acceptSkuPrice, getCart } from "../../../services/cart";
import { logSkuOutOfStockQuantity } from "../../../services/sku";

import Table from "../../Table/Table";
import TablePriceCell from "../../Table/TablePriceCell/TablePriceCell";
import QuantityControls from "../../QuantityControls/QuantityControls";
import SecondaryButton from "../../UI/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../UI/PrimaryButton/PrimaryButton";
import DeleteButton from "../../UI/DeleteButton/DeleteButton";
import LoadingOverlay from "../../UI/Loading/LoadingOverlay/LoadingOverlay";
import ErrorMessage from "../../UI/Error/ErrorMessage/ErrorMessage";
import { OutOfStockModal } from "../../UI/Modals";

import classNames from "classnames";
import classes from "./CartItems.module.css";

const CartItems = (props) => {
  const { skus, bundles, setCartState } = props;

  const [isAcceptPriceLoading, setIsAcceptPriceLoading] = useState(false);
  const [outOfStockModal, setOutOfStockModal] = useState({
    isOpen: false,
    skuId: null,
    skuImage: null,
    skuName: "",
    skuPrice: null,
    isCartRuleApplied: null,
  });

  const userId = useSelector(selectMechanicId);
  const {
    bundleQuantityIncreasedHandler,
    bundleQuantityDecreasedHandler,
    skuQuantityIncreasedHandler,
    skuQuantityDecreasedHandler,
    isLoading,
  } = useCart(setCartState);

  function closeOutOfStockModalHandler() {
    setOutOfStockModal({
      isOpen: false,
      skuId: null,
      skuImage: null,
      skuName: "",
      skuPrice: null,
      isCartRuleApplied: null,
    });
  }

  const increaseBundleQuantity = async (bundle) => {
    await bundleQuantityIncreasedHandler(
      bundle.id,
      bundle.requestedQuantity,
      1,
      bundle.skuList.map((sku) => ({ id: sku.skuId, quantity: sku.quantity }))
    );
  };

  const decreaseBundleQuantity = async (bundle) => {
    await bundleQuantityDecreasedHandler(
      bundle.id,
      bundle.requestedQuantity,
      1,
      bundle.skuList.map((sku) => ({ id: sku.skuId, quantity: sku.quantity }))
    );
  };

  const deleteBundle = async (bundle) => {
    await bundleQuantityDecreasedHandler(
      bundle.id,
      bundle.requestedQuantity,
      bundle.requestedQuantity,
      bundle.skuList.map((sku) => ({ id: sku.skuId, quantity: sku.quantity }))
    );
  };

  const increaseSkuQuantity = async (sku) => {
    if (sku.quantity >= sku.availableQuantity) {
      try {
        logSkuOutOfStockQuantity(sku.skuId, sku.quantity + 1);
        amplitude.track("order item quantity out of stock", {
          userId,
          skuId: sku.skuId,
          quantity: sku.quantity + 1,
        });
      } catch (err) {
        console.error(err);
      }
      setOutOfStockModal({
        isOpen: true,
        skuId: sku.skuId,
        skuImage: sku.imageUrl,
        skuName: sku.commercialName,
        skuPrice: sku.price,
        isCartRuleApplied: sku.isCartRuleApplied,
      });
      return;
    }
    await skuQuantityIncreasedHandler(
      sku.skuId,
      sku.suggestedSellingPrice,
      sku.quantity,
      1
    );
  };

  const decreaseSkuQuantity = async (sku) => {
    await skuQuantityDecreasedHandler(
      sku.skuId,
      sku.suggestedSellingPrice,
      sku.quantity,
      1
    );
  };

  const deleteSku = async (sku) => {
    await skuQuantityDecreasedHandler(
      sku.skuId,
      sku.suggestedSellingPrice,
      sku.quantity,
      sku.quantity
    );
  };

  const bundleRecords = bundles.map((bundle) => ({
    key: bundle.id,
    name: (
      <div className={classes["cart-items-table__item-name"]}>
        <p className={classes["cart-items-table__item-name-title"]}>
          مجموعة عرض
        </p>
        <ul className={classes["cart-items-table__bundle-skus-list"]}>
          {bundle.skuList.map((sku, index) => (
            <li
              key={index}
              className={classes["cart-items-table__bundle-sku-list-item"]}
            >
              <Link
                to={APP_PATHS.SKU_DETAILS.url.replace(":skuId", sku.skuId)}
                className={classes["cart-items-table__bundle-sku-link"]}
              >
                (<span className="numeric-content">{sku.quantity}</span>){" "}
                {sku.commercialName}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ),
    price: <TablePriceCell price={bundle.price} />,
    quantity: (
      <>
        <QuantityControls
          isPlusDisabled={!bundle.canAddMore}
          onPlus={() => increaseBundleQuantity(bundle)}
          onMinus={() => decreaseBundleQuantity(bundle)}
          quantity={bundle.requestedQuantity}
          className={classes["cart-items-table__quantity-controls"]}
        />
        {bundle.requestedQuantity > bundle.availableQuantity && (
          <ErrorMessage
            message="الكمية غير متاحة"
            className={classes["cart-items-table__quantity-error"]}
          />
        )}
      </>
    ),
    total: <TablePriceCell price={bundle.requestedQuantity * bundle.price} />,
    delete: (
      <DeleteButton
        onClick={() => deleteBundle(bundle)}
        className={classes["cart-items-table__delete-btn"]}
      />
    ),
  }));

  const acceptSkuPriceHandler = async (skuId, price) => {
    try {
      setIsAcceptPriceLoading(true);
      await acceptSkuPrice(skuId, price);
      const response = await getCart();
      const { cart } = response.data;
      setCartState(cart);
    } catch (err) {
      console.error(err);
    } finally {
      setIsAcceptPriceLoading(false);
    }
  };

  const skuRecords = skus.map((sku) => ({
    key: sku.skuId,
    name: (
      <>
        {sku.suggestedSellingPrice > sku.price && (
          <ErrorMessage
            message="تم تغيير سعر هذا المنتج"
            className={classes["cart-items-table__price-error"]}
          />
        )}
        <div className={classes["cart-items-table__item-name"]}>
          <p className={classes["cart-items-table__item-name-title"]}>
            {sku.brandName}
          </p>
          <Link
            to={APP_PATHS.SKU_DETAILS.url.replace(":skuId", sku.skuId)}
            className={classes["cart-items-table__sku-name"]}
          >
            {sku.commercialName}
          </Link>
        </div>
        {sku.suggestedSellingPrice > sku.price && (
          <>
            <p className={classes["cart-items-table__new-price-label"]}>
              السعر الجديد
            </p>
            <p className={classes["cart-items-table__new-price-wrapper"]}>
              <span
                className={classNames(
                  classes["cart-items-table__new-price"],
                  "numeric-content"
                )}
              >
                {sku.suggestedSellingPrice}
              </span>
              <span className={classes["cart-items-table__new-price-currency"]}>
                جم
              </span>
            </p>
            <div className={classes["cart-items-table__new-price-actions"]}>
              <SecondaryButton
                onClick={() => deleteSku(sku)}
                className={classes["cart-items-table__new-price-reject-btn"]}
              >
                رفض
              </SecondaryButton>
              <PrimaryButton
                onClick={() =>
                  acceptSkuPriceHandler(sku.skuId, sku.suggestedSellingPrice)
                }
                className={classes["cart-items-table__new-price-accept-btn"]}
              >
                قبول السعر
              </PrimaryButton>
            </div>
          </>
        )}
      </>
    ),
    price: <TablePriceCell price={sku.price} />,
    quantity: (
      <>
        <QuantityControls
          onPlus={() => increaseSkuQuantity(sku)}
          onMinus={() => decreaseSkuQuantity(sku)}
          quantity={sku.quantity}
          className={classes["cart-items-table__quantity-controls"]}
        />
        {sku.quantity > sku.availableQuantity && (
          <ErrorMessage
            message="الكمية غير متاحة"
            className={classes["cart-items-table__quantity-error"]}
          />
        )}
      </>
    ),
    total: <TablePriceCell price={sku.quantity * sku.price} />,
    delete: (
      <DeleteButton
        onClick={() => deleteSku(sku)}
        className={classes["cart-items-table__delete-btn"]}
      />
    ),
  }));

  return (
    <div className={classes["cart-items"]}>
      <header className={classes["cart-items__header"]}>
        <h2 className={classes["cart-items__title"]}>منتجات عربة التسوق</h2>
        <p className={classNames(classes["cart-items__quantity"])}>
          ({" "}
          <span className="numeric-content">
            {skus.length + bundles.length}
          </span>{" "}
          منتجات )
        </p>
      </header>
      <Table
        columns={CART_ITEMS_COLUMNS}
        data={[...bundleRecords, ...skuRecords]}
      />
      {(isLoading || isAcceptPriceLoading) && <LoadingOverlay />}
      {outOfStockModal.isOpen && (
        <OutOfStockModal
          isOpen={outOfStockModal.isOpen}
          onClose={closeOutOfStockModalHandler}
          skuId={outOfStockModal.skuId}
          imageUrl={outOfStockModal.skuImage}
          name={outOfStockModal.skuName}
          price={outOfStockModal.skuPrice}
          message="بنعتذر عن عدم توفر الكمية"
          showNotifyMe={!outOfStockModal.isCartRuleApplied}
        />
      )}
    </div>
  );
};

export default CartItems;

CartItems.propTypes = {
  skus: PropTypes.array,
  bundles: PropTypes.array,
  setCartState: PropTypes.func,
  setWeeklyBonus: PropTypes.func,
};
